// Este serviço não usa o mesmo cliente HTTP que o serviço da própria tela de negociação usa. A tela
// chama `api` de `src/lib`, com caminho relativo (`/suppliers`); aqui é o `api` de `src/utils/API`, e
// a URL é montada absoluta a partir da variável de ambiente `REACT_APP_IPA_URL`. Quem mexer aqui
// herda essa exceção — e ela não é única no módulo: o serviço da tela de negociações salvas segue o
// mesmo caminho. Fica escrito para ninguém gastar tempo procurando o motivo, nem supor que trocar
// para o cliente compartilhado é uma troca de uma linha.
import qs from 'qs';
import type { SuppliersExhibitionType } from '../store/useSuppliersExhibitionStore';
import api from '../utils/API';

export interface SuppliersModalRequest {
  // Página, tamanho e ordenação são campos do nível de cima porque não viajam no corpo:
  page: number;
  size: number;
  // Cada entrada é `campo,direção`. Lista vazia significa nenhuma coluna ordenada, e não produz
  // parâmetro na URL.
  sort: string[];
  productId: string;
  // Opcional porque no grão rede não existe cluster a informar: a linha da grade é o produto, e o
  // servidor não envia objeto de cluster algum. Enviar a chave nesse grão faz o serviço de
  // fornecedores recortar a busca por um cluster nulo, o que devolve página vazia sem erro.
  clusterId?: string;
  // Qual agrupamento a tela está exibindo.
  suppliersExhibitionType: SuppliersExhibitionType;
}

export interface StockData {
  lastUpdated: string;
  quantity: number;
  coverageDays: number;
}

export interface StoreData {
  storeId: string;
  stock?: StockData;
  cost: number;
  pmz: number;
  retailPrice: number;
  marginRetailPrice: number;
  competitivenessRetailPrice: number;
  competitorsPrice: number;
  marginCompetitorsPrice: number;
  expensesPercent?: number;
  salesTaxPercentage?: number;
  purchaseTaxPercentage?: number;
  // Opcional porque a régua de custo do contrato do cliente decide se ele existe:
  entryCost?: number;
  newObjectiveMargin?: number;
  objectiveMargin?: number | null;
  newCost?: number;
  storeName: string;
}

export interface SuppliersModalResponse {
  content: StoreData[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
  hasContent: boolean;
  first: boolean;
  last: boolean;
  numberOfElements: number;
  pageable: {
    sort: string[];
    offset: number;
    pageNumber: number;
    pageSize: number;
    unpaged: boolean;
    paged: boolean;
  };
  empty: boolean;
}

export interface ProductInfo {
  id: string;
  name: string;
  family: string;
  cluster: string;
  totalStores: number;
  objectiveMargin: number;
}

export const suppliersModalService = {
  async getStoresData(request: SuppliersModalRequest): Promise<SuppliersModalResponse> {
    const { page, size, sort, ...body } = request;

    try {
      const response = await api({
        url: `${process.env.REACT_APP_IPA_URL}/suppliers/modal`,
        method: 'POST',
        params: { page, size, sort },
        // A ordenação é lista, e a lista tem de sair com a chave repetida — é assim que a consulta da
        // grade, que pagina corretamente em produção hoje, já monta a URL. Sem isto a lista sairia
        // como índice numerado e o servidor não reconheceria o parâmetro. Lista vazia não produz
        // parâmetro nenhum.
        paramsSerializer(params: Record<string, unknown>) {
          return qs.stringify(params, { arrayFormat: 'repeat' });
        },
        data: body,
        expectToken: true,
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching stores data:', error);
      throw error;
    }
  },
};
